import { FormOption, ItemPreference } from "../../repositories/interfaces";

import { CatalogItem } from "../catalog/interface/CatalogItem";
import { Dictionary } from "../../models/Dictionary";
import { Form } from "./interface/Form";
import { FormField } from "./interface/FormField";
import { FormField as FormFieldModel } from "../../models/Form/FormField";
import FormLocales from "../../models/Form/FormLocales";
import { FormRule } from "../rules/interface/rulesInterface";
import { FormSection } from "./interface/FormSection";
import { LanguageItem } from "../languages/interface/LanguageItem";
import { Section } from "../../models/Section/Section";
import { v4 as uuidv4 } from "uuid";

export const cleanUndefinedValues = (obj: any): any => {
  if (obj === null || obj === undefined) {
    return null;
  }
  
  if (Array.isArray(obj)) {
    return obj.map(item => cleanUndefinedValues(item));
  }
  
  if (typeof obj === 'object') {
    const cleaned: any = {};
    for (const key in obj) {
      if (Object.hasOwn(obj, key)) {
        const value = obj[key];
        if (value !== undefined) {
          cleaned[key] = cleanUndefinedValues(value);
        }
      }
    }
    return cleaned;
  }
  
  return obj;
};

export const sectionsToSectionForms = (
  sectionsInterfaceList: Section[],
  fieldItems: Dictionary<CatalogItem[]>
) =>
  sectionsInterfaceList.map((sectionInterface: Section, index: number) => {
    const sectionForm = sectionToSectionForm(sectionInterface, index);
    const fieldList = fieldItems[sectionInterface.uuid];
    const formFields = fieldListToFormField(fieldList);
    sectionForm.formFields = formFields || [];
    return sectionForm;
  });

export const sectionToSectionForm = (
  sectionInterface: Section,
  index: number,
  t?: Function
): FormSection => {
  const weight = sectionInterface.weight ?? null;
  
  return {
    ...sectionInterface,
    title: t ? t(sectionInterface.defaultLanguageTitle) : sectionInterface.defaultLanguageTitle,
    weight,
    formFields: [],
    id: sectionInterface.id || 0,
    sectionId: sectionInterface.sectionId,
    score: sectionInterface.score,
    index,
    formSectionLocales: sectionInterface.formSectionLocales
  } as FormSection;
};

export const catalogItemToFormField = (catalogItem: CatalogItem, index: number, t?: Function) => {
  const options = catalogItem.options?.filter((option: any) => option?.option) || [];

  const formField: any = {
    ...catalogItem,
    type: catalogItem.type,
    weight: catalogItem.weight,
    score: catalogItem.score ? catalogItem.score : false,
    fieldId: catalogItem.fieldId,
    title: " ",
    mandatory: "false",
    defaultValue: "",
    id: catalogItem.id,
    options,
    preferences: catalogItem?.preferences,
    index
  };

  const parsedPreferences = parseItemPreferenceKeys(catalogItem.preferences);
  const reducedPreferences = reducePreferences(formField, parsedPreferences);
  const formFieldWithPreferences = {
    ...formField,
    ...reducedPreferences,
    options
  };
  formFieldWithPreferences.fieldId = removeSpaces(catalogItem.fieldId) || "";

  return formFieldWithPreferences;
};

export const reducePreferences = (formField: FormFieldModel, preferences: ItemPreference[]) => {
  const initialAccValue: any = {};
  const formFieldKeys = Object.keys(formField);
  const result = preferences?.reduce((accumulator, preference) => {
    const key = preference.key.toLowerCase();
    const preferenceKeyValue = { [key]: preference.value };
    return formFieldKeys.includes(preference.key)
      ? { ...accumulator, ...preferenceKeyValue }
      : {
          ...accumulator,
          preferences: { ...accumulator.preferences, ...preferenceKeyValue }
        };
  }, initialAccValue);
  result.preferences = JSON.stringify(result.preferences);
  return result;
};

export const parseItemPreferenceKeys = (preferences: ItemPreference[]) => {
  return preferences?.map((preference) => {
    let key = preference.key;
    if (
      preference.key === "ListItemsDropdown" ||
      preference.key === "ListItemsRadio" ||
      preference.key === "FixedListItemsDropdown"
    ) {
      key = "field-options";
    } else if (preference.key === "FixedTitle") {
      key = "title";
    }
    else if (preference.key === "Titledescription") {
      key = "description";
    }
    return { key: key.toLowerCase(), value: preference.value };
  });
};

export const removeSpaces = (str: string) => {
  const splitString = str?.split(" ");
  return splitString?.join("");
};

export const fieldListToFormField = (fieldsList: CatalogItem[] | null, t?: Function) => {
  return fieldsList?.map((field: CatalogItem, index: number) =>
    catalogItemToFormField(field, index, t)
  );
};

export const convertFormToSectionsAndFields = (formSections: FormSection[]) => {
  const sections: Section[] = [];
  const fields: Dictionary<CatalogItem[]> = {};
  return formSections?.reduce(
    (accumulator, formSection, index) => {
      const section: Section = {
        ...formSection,
        index,
        title: formSection.title,
        uuid: uuidv4(),
        score: !!formSection.weight,
        weight: formSection.weight,
        sectionId: formSection.sectionId,
        formSectionLocales: formSection.formSectionLocales
      };
      const fieldList = convertFormFieldsToCatalogItems(section.uuid, formSection.formFields);
      const items: Dictionary<CatalogItem[]> = {};
      items[section.uuid] = fieldList;
      const sections = [...accumulator.sections, section];
      const fields = { ...accumulator.fields, ...items };
      return { sections, fields };
    },
    { sections, fields }
  );
};

export const convertFormFieldsToCatalogItems = (uuid: string, formFields: FormField[]) => {
  const catalogList: CatalogItem[] = [];
  return formFields.reduce((accumulator, fieldItem, index) => {
    let hasScore = false;
    const preferences = parsePreferences(fieldItem);
    const options = preferences?.find(
      (preference) => preference.key === "ListItemsRadio" || preference.key === "ListItemsDropdown"
    );
    if (options) {
      let optionObj = [] as FormOption[];

      if (options.value && options.value !== "null") {
        optionObj = JSON.parse(options?.value) as FormOption[];
      }
      hasScore = optionObj.some((option) => option.weight);
    }
    const field: CatalogItem = {
      ...fieldItem,
      description: "",
      fieldListUuid: uuid,
      icon: "",
      id: fieldItem.id,
      fieldId: fieldItem.fieldId,
      index,
      name: fieldItem.title,
      type: fieldItem.type,
      preferences: preferences,
      uuid: uuidv4(),
      relation: fieldItem.preferences ? JSON.parse(fieldItem?.preferences).relation : null,
      score: !!fieldItem.weight || hasScore,
      weight: fieldItem.weight,
      formFieldLocales: fieldItem.formFieldLocales
    };
    return [...accumulator, field];
  }, catalogList);
};

export const parsePreferences = (formField: FormField) => {
  const object: any = formField;
  const treatedObject = treatFormFieldObject(object);
  const keys = Object.keys(treatedObject);
  return keys.map((key) => {
    const preference: ItemPreference = { key, value: treatedObject[key] };
    return preference;
  });
};

export const treatFormFieldObject = (object: any) => {
  const keys = Object.keys(object);
  let newObject: any = {};
  const keysToReturn = new Set([
    "title",
    "options",
    "mandatory",
    "preferences",
    "fieldOptions",
    "description",
  ]);
  const filteredKeys = keys.filter((key) => keysToReturn.has(key));
  for (let key of filteredKeys) {
    if (key === "options") {
      const convertedKey = convertOptions(object);
      if (convertedKey) {
        let options = JSON.stringify(object[key]);

        newObject[convertedKey] = options;
      }
    } else if (key === "preferences") {
      const convertedPreferences = convertPreferences(object[key]);
      newObject = { ...newObject, ...convertedPreferences };
    }
    else if (key === "description"){
      newObject["Titledescription"] = object[key];
    }
    else {
      const convertedKey = camelCase(key);
      newObject[convertedKey] = object[key];
    }
  }
  return newObject;
};

const convertOptions = (object: any) => {
  if (object["type"] === "dropdown" || object["type"] === "check-list") {
    return "ListItemsDropdown";
  } else if (
    object["type"] === "dropdown-campaign" ||
    object["type"] === "dropdown-customer" ||
    object["type"] === "dropdown-equipment" ||
    object["type"] === "dropdown-campaign-end-reason"
  ) {
    return "FixedListItemsDropdown";
  } else if (object["type"] === "boolean") {
    return "ListItemsRadio";
  }
  return null;
};

const convertPreferences = (object: string | null) => {
  const preferencesJSON = object ? JSON.parse(object) : {};
  const convertedPreferences: any = {};
  for (let key in preferencesJSON) {
    if (key === "equipmentmrk") {
      const convertedKey = "EquipmentMRK";
      convertedPreferences[convertedKey] = preferencesJSON[key];
    } else {
      const upperCasedKey = camelCase(key);
      convertedPreferences[upperCasedKey] = preferencesJSON[key];
    }
  }
  return convertedPreferences;
};

export const camelCase = (value: string) => {
  const splitValue = value.split("");
  splitValue[0] = splitValue[0].toUpperCase();
  return splitValue.join("");
};

export const download = (content: any, fileName: string, contentType: string) => {
  const a = document.createElement("a");
  const file = new Blob([content], { type: contentType });
  a.href = URL.createObjectURL(file);
  a.download = fileName;
  a.click();
};

export const selectLanguageByCultureId = (languageList: LanguageItem[], cultureId: number) => {
  let language = languageList?.find((language: LanguageItem) => language.id === cultureId);
  return language;
};

export const selectCultureIdByIsoCode = (languageList: LanguageItem[], isoCode: string) => {
  let cultureId = languageList?.find((language: LanguageItem) => language.isoCode === isoCode)?.id;
  return cultureId;
};

export const mapFormRules = (formRules: FormRule[]) => {
  return formRules.map((rule: FormRule, index: number) => ({
    name: rule.name,
    id: rule.id,
    formId: rule.formId,
    orderIndex: rule.orderIndex,
    agent: rule.agent,
    agentType: rule.agentType,
    logicalTest: rule.logicalTest,
    response: rule.response,
    action: rule.action,
    target: rule.target,
    targetType: rule.targetType,
    targetOptions: rule.targetOptions,
    index
  }));
};

export const stringfyFormRulesTargetOptions = (formRules: FormRule[]) => {
  return formRules.map((rule: FormRule) => ({
    ...rule,
    targetOptions: JSON.stringify(rule.targetOptions)
  }));
};

export const parseFormApiToStore = (form: Form, languageList: LanguageItem[]) => {
const item = structuredClone(form);

processFormMainData(item, languageList);
processFormSections(item);
processFormRules(item);

return item;
};

const processFormMainData = (item: any, languageList: LanguageItem[]) => {
const originalTitles = [...item.title];

const defaultLocale = findDefaultLocale(item, languageList);

if (defaultLocale) {
  item.title = defaultLocale.title;
  item.description = defaultLocale.description;
}

const nonDefaultTitles = filterNonDefaultItems(originalTitles, item.defaultLanguage);
addLocalizedProperties(item, nonDefaultTitles, 'title');
};

const processFormSections = (item: any) => {
item.formSections = item.formSections?.map((section: any) => {
  processLocalizableProperty(section, item.defaultLanguage, 'title');
  processSectionFields(section, item.defaultLanguage);
  return section;
});
};

const processSectionFields = (section: any, defaultLanguage: string) => {
for (const field of section.formFields ?? []) {
  processLocalizableProperty(field, defaultLanguage, 'title');
  processLocalizableProperty(field, defaultLanguage, 'description');
  processFieldOptions(field, defaultLanguage);
}
};

const processFieldOptions = (field: any, defaultLanguage: string) => {
for (const option of field.options ?? []) {
  const originalOptions = [...(option.option || [])];
  
  const defaultValue = findDefaultValue(option.option, defaultLanguage);
  option.option = defaultValue;
  
  const nonDefaultItems = filterNonDefaultItems(originalOptions, defaultLanguage);
  addLocalizedProperties(option, nonDefaultItems, 'title');
}
};

const processLocalizableProperty = (obj: any, defaultLanguage: string, property: string) => {
if (!obj[property]) return;

const originalItems = [...obj[property]];

const defaultValue = findDefaultValue(obj[property], defaultLanguage);
obj[property] = defaultValue;

const nonDefaultItems = filterNonDefaultItems(originalItems, defaultLanguage);
addLocalizedProperties(obj, nonDefaultItems, property);
};

const findDefaultLocale = (item: any, languageList: LanguageItem[]) => {
return item.formLocales?.find((locale: FormLocales) =>
  selectLanguageByCultureId(languageList, locale.cultureId)?.isoCode === item.defaultLanguage
);
};

const findDefaultValue = (items: any[], defaultLanguage: string) => {
if (!items?.length) return undefined;

const defaultItem = items.find((item: any) => item.lang === defaultLanguage);
return defaultItem?.value ?? items[0]?.value;
};

const filterNonDefaultItems = (items: any[], defaultLanguage: string) => {
if (!Array.isArray(items)) return [];
return items.filter((item: any) => item.lang !== defaultLanguage);
};

const addLocalizedProperties = (obj: any, items: any[], prefix: string) => {
for (const item of items) {
  obj[`${prefix}-${item.lang}`] = item.value;
}
};

const processFormRules = (item: any) => {
for (const rule of item.formRules ?? []) {
  if (rule?.targetOptions) {
    rule.targetOptions = JSON.parse(rule.targetOptions);
  }
}
};

const _configActiveLanguages = (
  activeLanguages:LanguageItem[],
  locales:any,
  defaultLocaleTitle:string,
  languagesList:LanguageItem[],
  id:number,
  defaultLocaleDescription?:string
  )=>{
  for (const activeLanguage of activeLanguages ?? []) {
    let locale;

    if(locales){
      locale = locales?.find(
        (locale: any) =>
          selectLanguageByCultureId(languagesList, locale.cultureId)?.isoCode ===
          activeLanguage.isoCode
      );
    }else{
      locales = [];
    }

    if(locale && (locale.title===""|| locale.title===" " || locale.title===undefined)){
      locale.title = defaultLocaleTitle || "";
    }
    if(locale && (locale.description===""|| locale.description===" " || locale.description===undefined)){
      locale.description = defaultLocaleDescription || "";
    }

    if (!locale) {
      locales.push({
        cultureId: selectCultureIdByIsoCode(
          languagesList,
          activeLanguage.isoCode
        ),
        formId: id,
        title: defaultLocaleTitle || "",
        description: defaultLocaleDescription || "",
      });
    }

  }
  return locales;
}

const _getOldTitleObj = (activeLanguages: LanguageItem[], title: any, value: string) => {
  const existingLangs = new Set(title.map((t: any) => t.lang));
  
  for (const activeLanguage of activeLanguages ?? []) {
    if (!existingLangs.has(activeLanguage.isoCode)) {
      //compatibilidade com form antigo
      title.push({
        lang: activeLanguage.isoCode,
        value: value
      });
    }
  }
  return title;
};

const _getDefaultLanguageTitle = (
  title: string | any[],
  defaultLanguageId: number,
  locales: any,
  t?: Function
) => {
  if (Array.isArray(title) || typeof title !== 'string') {
    return locales?.find((locale: any) => locale.id === defaultLanguageId)?.title || "";
  }
  
  let formTitleDefaultLanguage: string = t ? t(title) : title;

  if (formTitleDefaultLanguage === "" || formTitleDefaultLanguage === " ") {
    formTitleDefaultLanguage = locales?.find(
      (locale: any) => locale.id === defaultLanguageId
    )?.title || "";
  }
  return formTitleDefaultLanguage || "";
};

const _getDefaultLanguageDescription = (description: string | any[], defaultLanguageId:number, locales:any ,t?: Function)=>{
  if (Array.isArray(description) || typeof description !== 'string') {
    return locales?.find((locale:any) => locale.id === defaultLanguageId)?.description || "";
  }

  let fieldDescriptionDefaultLanguage:string = t ? t(description) : description;

  if(fieldDescriptionDefaultLanguage===""|| fieldDescriptionDefaultLanguage===" "){
    fieldDescriptionDefaultLanguage = locales?.find((locale:any)=>
      locale.id===defaultLanguageId
    )?.description || "";
  }
  return fieldDescriptionDefaultLanguage || "";
}

const _configurePreferences = (preferences: any, defaultFormLanguageId:number) => {
  try{
    if (!preferences['field-options'] || preferences['field-options'] === 'undefined') {
      return preferences;
    }
    
    let fieldOptions:any[] = JSON.parse(preferences['field-options']);

    if (fieldOptions && Array.isArray(fieldOptions)) {
      for (const option of fieldOptions) {
        let defaultOptionLocale = option.formOptionLocales?.find(
          (locale: any) => locale.cultureId === defaultFormLanguageId
        );

        for (const locale of option.formOptionLocales ?? []) {
          if (!locale.option || locale.option === "" || locale.option === " ") {
            locale.option = defaultOptionLocale?.option || "";
          }
        }
      }
      
      preferences["field-options"] = JSON.stringify(fieldOptions);
    }
    return preferences;
  } catch (ex) {
    console.error('Erro ao configurar preferências de campo:', ex);
    if (preferences['field-options']) {
      delete preferences['field-options'];
    }
  }
  return preferences;
};

const configureFieldOptions = (field: any) => {
  const isSARField =
    field?.name === "fields.equipment" ||
    field?.name === "fields.customer" ||
    field?.name === "fields.campaign" ||
    field?.name === "fields.end_reason";
  try {
    if (isSARField) {
      let fieldOptionsParsed = JSON.parse(field?.fieldOptions);
      if (fieldOptionsParsed) {
        field.options = fieldOptionsParsed;
      }
    } else if (field.preferences) {
      let preferencesParsed = JSON.parse(field?.preferences);
      if (preferencesParsed["field-options"]) {
        let realOptions = JSON.parse(preferencesParsed["field-options"]);
        field.options = realOptions;
      }
    }
    return field;
  } catch (ex) {
    console.error("Error in parse fieldOptions");
    return field;
  }
};

function processOptionLocales(
  option: any,
  activeLanguages: LanguageItem[],
  languagesList: LanguageItem[],
  defaultLanguageIsoCode: string,
  optionTitleDefaultLanguage: string,
  formId: number,
  t?: Function
) {
  option.option = [{ lang: defaultLanguageIsoCode, value: optionTitleDefaultLanguage }];

  for (const activeLanguage of activeLanguages ?? []) {
    const fieldOptionLocale = option.formOptionLocales?.find(
      (locale: any) =>
        selectLanguageByCultureId(languagesList, locale.cultureId)?.isoCode ===
        activeLanguage.isoCode
    );

    if (!fieldOptionLocale) {
      option.formOptionLocales = option.formOptionLocales || [];
      option.formOptionLocales.push({
        cultureId: selectCultureIdByIsoCode(languagesList, activeLanguage.isoCode),
        formOptionId: option.id,
        option: option[`title-${activeLanguage.isoCode}`] || optionTitleDefaultLanguage
      });
    } else if (fieldOptionLocale.option === "") {
      fieldOptionLocale.option = optionTitleDefaultLanguage;
    }

    option.option.push({
      lang: activeLanguage.isoCode,
      value: option[`title-${activeLanguage.isoCode}`] || optionTitleDefaultLanguage
    });
  }

  option.formId = formId;
}

function processField(config: {
  field: any;
  indexField: number;
  form: any;
  activeLanguages: LanguageItem[];
  languagesList: LanguageItem[];
  defaultLanguageIsoCode: string;
  defaultLanguageId: number;
  t?: Function;
}) {
  let {
    field,
    indexField,
    form,
    activeLanguages,
    languagesList,
    defaultLanguageIsoCode,
    defaultLanguageId,
    t
  } = config;

  const fieldTitleDefaultLanguage = _getDefaultLanguageTitle(field.title, defaultLanguageId, field.formFieldLocales, t);

  field.title = [
    {
      lang: defaultLanguageIsoCode,
      value: fieldTitleDefaultLanguage
    },
  ];
  const fieldDescriptionDefaultLanguage = _getDefaultLanguageDescription(field.description, defaultLanguageId, field.formFieldLocales, t);

  field.description = [
    {
      lang: defaultLanguageIsoCode,
      value: fieldDescriptionDefaultLanguage
    },
  ];

  field.index = indexField;
  field.formId = form.formId;

  _configActiveLanguages(activeLanguages, field.formFieldLocales, fieldTitleDefaultLanguage, languagesList, field.id, fieldDescriptionDefaultLanguage);

  _getOldTitleObj(activeLanguages, field.title, fieldTitleDefaultLanguage ?? "");

  if (typeof field.options === "string") {
    field.options = [];
  }
  field = configureFieldOptions(field);

  const preferenceToSave = _configurePreferences(
    JSON.parse(field?.preferences),
    defaultLanguageId
  );
  field.preferences = JSON.stringify(preferenceToSave);

  for (const [indexOpt, option] of (field.options ?? []).entries()) {
    const optionTitleDefaultLanguage = t ? t(option.option) : option.option;
    processOptionLocales(
      option,
      activeLanguages,
      languagesList,
      defaultLanguageIsoCode,
      optionTitleDefaultLanguage,
      form.formId,
      t
    );
    option.index = indexOpt;
  }

  field.fieldOptions = field.options;
}

function processSection(config: {
  section: any;
  indexSection: number;
  form: any;
  activeLanguages: LanguageItem[];
  languagesList: LanguageItem[];
  defaultLanguageIsoCode: string;
  defaultLanguageId: number;
  t?: Function;
}) {
  const {
    section,
    indexSection,
    form,
    activeLanguages,
    languagesList,
    defaultLanguageIsoCode,
    defaultLanguageId,
    t
  } = config;

  const sectionTitleDefaultLanguage = _getDefaultLanguageTitle(
    section.title,
    defaultLanguageId,
    section.formSectionLocales,
    t
  );

  const sectionDescriptionDefaultLanguage = _getDefaultLanguageDescription(
    section.description,
    defaultLanguageId,
    section.formSectionLocales,
    t
  );

  section.title = [{ lang: defaultLanguageIsoCode, value: sectionTitleDefaultLanguage }];
  section.index = indexSection;
  section.formId = form.formId;

  _configActiveLanguages(
    activeLanguages,
    section.formSectionLocales,
    sectionTitleDefaultLanguage,
    languagesList,
    section.id,
    sectionDescriptionDefaultLanguage
  );

  _getOldTitleObj(activeLanguages, section.title, sectionTitleDefaultLanguage ?? "");

  for (const [indexField, field] of (section.formFields ?? []).entries()) {
    processField({
      field,
      indexField,
      form,
      activeLanguages,
      languagesList,
      defaultLanguageIsoCode,
      defaultLanguageId,
      t
    });
  }
}

export const parseFormStoreToApi = (
  form: any,
  languagesList: LanguageItem[],
  activeLanguages: LanguageItem[],
  t?: Function
) => {
  try {
    const cleanedForm = cleanUndefinedValues(form);
    
    const englishLocaleId = 2;
    const defaultLanguageIsoCode = cleanedForm.defaultLanguage;
    const defaultLanguageId =
      languagesList?.find((language: LanguageItem) => language.isoCode === defaultLanguageIsoCode)
        ?.id ?? englishLocaleId;

    const formTitleDefaultLanguage = _getDefaultLanguageTitle(
      cleanedForm.title,
      defaultLanguageId,
      cleanedForm.formLocales,
      t
    );

    delete cleanedForm.title;
    cleanedForm.title = [{ lang: defaultLanguageIsoCode, value: formTitleDefaultLanguage }];

    cleanedForm.formLocales = _configActiveLanguages(
      activeLanguages,
      cleanedForm.formLocales,
      formTitleDefaultLanguage,
      languagesList,
      cleanedForm.id
    );
    cleanedForm.title = _getOldTitleObj(activeLanguages, cleanedForm.title, formTitleDefaultLanguage ?? "");

    const descriptionValue = Array.isArray(cleanedForm.description) 
      ? "" 
      : (cleanedForm.description || "");
    
    cleanedForm.description = [
      {
        lang: defaultLanguageIsoCode,
        value: descriptionValue
      }
    ];

    for (const [indexSection, section] of (cleanedForm.formSections ?? []).entries()) {
      processSection({
        section,
        indexSection,
        form: cleanedForm,
        activeLanguages,
        languagesList,
        defaultLanguageIsoCode,
        defaultLanguageId,
        t
      });
    }

    return cleanUndefinedValues(cleanedForm);
  } catch (error) {
    console.error('Erro ao processar formulário para API:', error);
    throw new Error('Erro na serialização do formulário. Verifique os dados do formulário.');
  }
};
